import React from "react";
import { X, Edit3, Save, ChevronLeft } from "lucide-react";
import Button from "../../../common/Button";
import "./DetailHeader.css";

/**
 * DetailHeader Component
 *
 * Reusable header for detail/edit views with back, edit, and save actions
 *
 * @param {String} title - Header title text
 * @param {Node} subtitle - Optional content rendered directly under the title
 * @param {Function} onBack - Callback for back/close button
 * @param {Function} onEdit - Callback for edit button (toggles edit mode)
 * @param {Function} onSave - Callback for save button
 * @param {Boolean} isEditing - Whether currently in edit mode
 * @param {Boolean} canEdit - Whether edit functionality should be shown (default: true)
 * @param {Boolean} saveDisabled - Whether save button is disabled
 * @param {String} backLabel - Breadcrumb text for the back link, e.g. "Back to Users".
 *   When provided, renders a "‹ {backLabel}" link above the title instead of the bare X button.
 * @param {String} editLabel - Label for edit button (default: "Edit")
 * @param {String} cancelLabel - Label for cancel button when editing (default: "Cancel")
 * @param {String} saveLabel - Label for save button (default: "Save")
 */
export default function DetailHeader({
  title,
  subtitle,
  onBack,
  onEdit,
  onSave,
  isEditing = false,
  canEdit = true,
  showBack = true,
  saveDisabled = false,
  backLabel,
  editLabel = "Edit",
  cancelLabel = "Cancel",
  saveLabel = "Save",
}) {
  const actions = (
    <div className="detail-header-actions">
      {canEdit && onEdit && (
        <Button
          className={isEditing ? "button--plain" : "button--primary"}
          onClick={onEdit}
        >
          {isEditing ? (
            <>
              <X size={16} style={{ marginRight: "6px" }} />
              {cancelLabel}
            </>
          ) : (
            <>
              <Edit3 size={16} style={{ marginRight: "6px" }} />
              {editLabel}
            </>
          )}
        </Button>
      )}
      {isEditing && !onEdit && onBack && (
        <Button className="button--plain" onClick={onBack}>
          <X size={16} style={{ marginRight: "6px" }} />
          {cancelLabel}
        </Button>
      )}
      {isEditing && onSave && (
        <Button
          className="button--primary detail-header-save"
          onClick={onSave}
          disabled={saveDisabled}
        >
          <Save size={16} style={{ marginRight: "6px" }} />
          {saveLabel}
        </Button>
      )}
    </div>
  );

  if (backLabel) {
    return (
      <>
        {showBack && onBack && (
          <div className="detail-header-breadcrumb-row">
            <button
              type="button"
              className="detail-header-back-link"
              onClick={onBack}
            >
              <ChevronLeft size={16} />
              {backLabel}
            </button>
          </div>
        )}
        <div className="detail-header">
          <div className="detail-header-content detail-header-content--breadcrumb">
            <div className="detail-header-main">
              <div className="detail-header-title-group">
                <h2 className="detail-header-title">{title}</h2>
                {subtitle && (
                  <div className="detail-header-subtitle">{subtitle}</div>
                )}
              </div>
              {actions}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="detail-header">
      <div className="detail-header-content">
        <div className="detail-header-left">
          {showBack && onBack && (
            <button
              type="button"
              className="detail-header-back-button"
              onClick={onBack}
              aria-label="Go back"
            >
              <X size={24} />
            </button>
          )}
          <div className="detail-header-title-group">
            <h2 className="detail-header-title">{title}</h2>
            {subtitle && (
              <div className="detail-header-subtitle">{subtitle}</div>
            )}
          </div>
        </div>
        {actions}
      </div>
    </div>
  );
}
